import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import MainLayout from "../Themes/MainLayout";
import MuiAlert from "@mui/material/Alert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { hasPermission } from "../utils/auth";
import { formatDateLocal } from "../utils/dateUtils";
import {
  TextField,
  Tooltip,
  Pagination,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CssBaseline,
} from "@mui/material";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  InputProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Snackbar,
  Alert,
  Popover,
  InputAdornment,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Flag,
  FilterList,
  Settings,
  Search,
} from "@mui/icons-material";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const empAssetFields = [
  "employee_name",
  "asset_description",
  "model_no",
  "model_name",
  "serial_no",
  "warranty_validity",
  "asset_condition",
];
const emppopupFields = ["employee_id", "asset_id"];
const empfieldLabels = {
  employee_id: "Employee",
  employee_name: "Employee Name",
  asset_id: "Asset",
  asset_description: "Asset Description",
  model_no: "Model No",
  model_name: "Model Name",
  serial_no: "Serial No",
  warranty_validity: "Warranty Validity",
  asset_condition: "Asset Condition",
};
const EmployeeAssets = () => {
  const { loading, userId, empId, boId, roles, hasPermission } =
    useCurrentUser();
  const bo_id = boId;
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetDetails, setAssetDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // Added isEditing state
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // or "error"
  const [assets, setAssets] = React.useState([]);
  const [assetName, setAssetName] = useState({});
  const [empName, setEmpName] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [filters, setFilters] = useState({ deleteFlag: "ACTIVE" });
  const [anchorEls, setAnchorEls] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteAssetId, setDeleteAssetId] = useState(null);
  const [errors, setErrors] = useState({
    asset_id: false,
    employee_id: false,
  });
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    empAssetFields.forEach((f) => (initial[f] = true));
    return initial;
  });
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newAsset, setNewAsset] = useState({
    delete_flag: "N",
    data_source: "UI",
    created_by: "system",
    created_on_date: new Date().toISOString().split("T")[0],
  });
  const [empEditAsset, setEditEmpAsset] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [search, setSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for Add form
  // Return Asset Dialog States
  const [returnConfirmOpen, setReturnConfirmOpen] = useState(false);
  const [returnRow, setReturnRow] = useState(null);
  const [returnForm, setReturnForm] = useState({
    asset_option: "",
    returned_on: "",
    returned_to: "",
    asset_id: "",
    asset_complaints: "",
    replaced_on: "",
    remarks: "",
  });
  const [returnErrors, setReturnErrors] = useState({
    asset_option: false,
    returned_on: false,
    returned_to: false,
    asset_complaints: false,
    replaced_on: false,
    asset_id: false,
    remarks: "", // Add remarks error message
  });
  const [assetComplaintsOptions, setAssetComplaintsOptions] = useState([]);
  const [returnAssetIdOptions, setReturnAssetIdOptions] = useState([]);
  const [returnEmpIdOptions, setReturnEmpIdOptions] = useState([]);
  const rowsPerPage = 10;
  const resetForm = () => {
    setNewAsset({
      bo_id,
      created_by: empId,
      modified_by: empId,
      created_on_date: new Date(),
    });
    setErrors({});
    setIsEditing(false);
  };
  const fetchempAssets = useCallback(async () => {
    try {
      let res, json;

      const normalizeRoles = (roles) => {
        if (!roles) return [];
        return Array.isArray(roles)
          ? roles.map((r) => r.toLowerCase())
          : [roles.toLowerCase()];
      };
      const normalizedRoles = normalizeRoles(roles);
      // Check for privileged roles first (Admin/HR see all employee assets)
      if (Array.isArray(roles) && 
          (['admin', 'admins', 'hr', 'hrs', 'human resource', 'human resources'].some((role) => normalizedRoles.includes(role)))) {
        const query = new URLSearchParams({
          page: page + 1,
          limit: rowsPerPage,
          ...filters,
          search: search.trim(),
          bo_id,
        }).toString();
        res = await fetch(`${BASE_URL}/EmployeeAssets/paginated?${query}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Fetch failed with status ${res.status}: ${text}`);
        }
        json = await res.json();
        setAssets(Array.isArray(json.data) ? json.data : []);
        setTotalCount(json.total || 0);
      } else if (Array.isArray(roles) && normalizedRoles.includes("employees")) {
        // Regular employee sees only their own asset records
        const query = new URLSearchParams({
          page: page + 1,
          limit: rowsPerPage,
          ...filters,
          search: search.trim(),
          bo_id,
          empId,
        }).toString();
        res = await fetch(`${BASE_URL}/EmployeeAssets/paginated?${query}`);
        if (res.status === 404) {
          setAssets([]);
          setErrorMessage("No records found");
          setTimeout(() => setErrorMessage(""), 3000);
          setTotalCount(0);
          return;
        }
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Fetch failed with status ${res.status}: ${text}`);
        }
        json = await res.json();
        setAssets(Array.isArray(json.data) ? json.data : []);
        setTotalCount(json.total || 0);
      } else {
        // No access - no matching roles
        setAssets([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching employee assets:", err);
      setAssets([]);
      setTotalCount(0);
    }
  }, [page, filters, search, bo_id, empId, roles]);

  useEffect(() => {
    fetchempAssets();
  }, [fetchempAssets]);

  useEffect(() => {
    setPage(0);
  }, [search, filters, bo_id]);

  // Fetch asset options when return dialog opens
  useEffect(() => {
    if (returnConfirmOpen && bo_id) {
      // Fetch available assets
      axios
        .get(`${BASE_URL}/EmployeeAssets/getAllAssetIds/${bo_id}`)
        .then((res) => {
          setReturnAssetIdOptions(res.data);
        })
        .catch((err) =>
          console.error("Error fetching asset options for return:", err),
        );

      // Fetch employee options for "Handed Over To" dropdown (Admin/HR only)
      axios
        .get(`${BASE_URL}/EmployeeAssets/getAllEmployeeIds/${bo_id}?roleFilter=admin-hr`)
        .then((res) => {
          setReturnEmpIdOptions(res.data);
        })
        .catch((err) =>
          console.error("Error fetching employee options for return:", err),
        );
    }
  }, [returnConfirmOpen, bo_id]);

  // Fetch asset complaints options
  useEffect(() => {
    const fetchAssetComplaints = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/commonVariables/assetReturnReason`,
        );
        const data = await res.json();
        setAssetComplaintsOptions(data);
      } catch (err) {
        console.error("Failed to fetch asset complaints:", err);
      }
    };
    fetchAssetComplaints();
  }, []);

  const [empIdOptions, setempIdOptions] = useState([]); // For Employee ID dropdown
  const [assetIdOptions, setassetIdOptions] = useState([]);

  useEffect(() => {
    if (open) {
      axios
        .get(`${BASE_URL}/EmployeeAssets/getAllEmployeeIds/${bo_id}`)
        .then((res) => {
          setempIdOptions(res.data);
        })
        .catch((err) => console.error("Error fetching dropdown options:", err));
      axios
        .get(`${BASE_URL}/EmployeeAssets/getAllAssetIds/${bo_id}`)
        .then((res) => {
          setassetIdOptions(res.data);
        })
        .catch((err) => console.error("Error fetching dropdown options:", err));
    }
  }, [open, bo_id]);

  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };
    fetchStatusFlags();
  }, []);

  const handleAssetChange = async (event) => {
    const assetId = event.target.value; // directly asset_id now
    try {
      const res = await axios.get(
        `${BASE_URL}/EmployeeAssets/getAssetDetails/${assetId}/${bo_id}`,
      );
      const details = res.data;
      setAssetDetails(details);
    } catch (err) {
      console.error("Error fetching asset details:", err);
      setAssetDetails(null);
    }
  };

  const renderField = (
    field,
    state,
    setState,
    isEditMode = false,
    customOnChange,
  ) => {
    const handleChange = (e) => {
      const { name, value } = e.target;
      setState((prev) => ({ ...prev, [name]: value }));

      if (customOnChange) {
        customOnChange(e);
      }
    };
    // Always render these fields as dropdown
    const isDropdownField = ["employee_id", "asset_id"].includes(field);
    let options = [];
    if (field === "employee_id") {
      options = empIdOptions;
    }
    if (field === "asset_id") {
      if (!isEditMode) {
        // Add mode: only show assets with status "Available"
        options = assetIdOptions.filter(
          (opt) => opt.asset_status === "Available",
        );
      } else {
        // Edit mode: show all available assets + the currently selected asset
        const currentAssetId = state.asset_id;
        options = assetIdOptions.filter(
          (opt) =>
            opt.asset_status === "Available" || opt.asset_id === currentAssetId,
        );
      }
    }
    return (
      <TextField
        select={isDropdownField}
        label={empfieldLabels[field] || field}
        name={field}
        value={state[field] || ""}
        onChange={(e) => {
          handleChange(e);
          // Clear error when user selects something
          setErrors((prev) => ({ ...prev, [field]: false }));
        }}
        required
        error={errors[field]} //  Show red border
        helperText={
          errors[field]
            ? `Please select ${field === "employee_id" ? "Employee" : "Asset"}`
            : ""
        } // Show error text
        size="small"
        margin="dense"
        fullWidth
        InputLabelProps={{ shrink: true }} // forces label above
        sx={{
          fontSize: "12px",
          mb: 1,
          "& .MuiInputBase-input, & .MuiInputLabel-root": {
            fontSize: "14px",
          },
          "& .MuiInputBase-root": {
            fontSize: "12px",
            width: "200px",
          },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#1976d2" },
            "&.Mui-focused fieldset": { borderColor: "#1976d2" },
            height: "35px",
          },
        }}
      >
        {isDropdownField &&
          options.map((opt) => (
            <MenuItem
              key={opt.employee_id || opt.asset_id}
              value={opt.employee_id || opt.asset_id} // only send the ID to backend
              sx={{ fontSize: "12px" }}
            >
              {opt.employee_id
                ? `${opt.employee_id} - ${opt.employee_name}`
                : `${opt.asset_id} - ${opt.asset_description}`}
            </MenuItem>
          ))}
      </TextField>
    );
  };
  const handleAdd = async () => {
    // Prevent multiple API calls on single click
    if (isSubmitting) return;
    const newErrors = {
      asset_id: !newAsset.asset_id && !newAsset.assetId,
      employee_id: !newAsset.employee_id,
    };

    setErrors(newErrors);

    // If any field has an error, stop the function
    if (newErrors.asset_id || newErrors.employee_id) {
      return;
    }
    setIsSubmitting(true); // Disable button and prevent double-click
    try {
      await axios.post(`${BASE_URL}/EmployeeAssets`, {
        ...newAsset,
        bo_id, // include existing field
        asset_id: newAsset.asset_id || newAsset.assetId,
        created_by: empId, // Add empId as created_by
        modified_by: empId, // Add empId as modified_by
      });
      setAssetDetails(null);
      setOpen(false);
      setSuccessMessage("Record added successfully");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (err) {
      console.error("Add failed:", err.response?.data || err.message);
      // Get error message from response
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message ||
        "Failed to add record";
      setSnackbarMsg(errorMsg);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setIsSubmitting(false); // Re-enable button after API response
    }
  };

  const handleEdit = async () => {
    try {
      const { employee_id, asset_id, emp_asset_id } = empEditAsset;
      await axios.put(`${BASE_URL}/EmployeeAssets`, {
        bo_id,
        employee_id,
        asset_id,
        emp_asset_id,
        modified_by: empId, // Add empId as modified_by
      });
      setEditOpen(false);
      setSuccessMessage("Record updated successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message);
      setErrorMessage(err.message || "Update failed..");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };
  const handleFilterIconClick = (field, event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };
  const handleFilterClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };
  const handleDelete = (
    emp_asset_id,
    asset_id,
    employee_name,
    asset_description,
  ) => {
    setDeleteId(emp_asset_id);
    setDeleteAssetId(asset_id);
    setEmpName(employee_name);
    setAssetName(asset_description);
    setConfirmOpen(true);
  };
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${BASE_URL}/EmployeeAssets/${deleteId}/${deleteAssetId}/${bo_id}`,
      );
      setSuccessMessage("Record Deleted Successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      setErrorMessage(err.message || "Delete failed..");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setDeleteAssetId(null);
    }
  };
  const handleToggleFlag = async (emp_asset_id, currentFlag) => {
    const newFlag = currentFlag === "N" ? "Y" : "N";
    // Optimistic UI update
    setAssets((prevAssets) =>
      prevAssets.map((asset) =>
        asset.emp_asset_id === emp_asset_id
          ? { ...asset, delete_flag: newFlag }
          : asset,
      ),
    );
    try {
      const res = await fetch(
        `${BASE_URL}/EmployeeAssets/${emp_asset_id}/${bo_id}/toggle-flag`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ currentFlag }),
        },
      );
      if (!res.ok) {
        const errorText = await res.text();
        console.error("Toggle flag error:", res.status, errorText);
        // revert on error
        setAssets((prevAssets) =>
          prevAssets.map((asset) =>
            asset.emp_asset_id === emp_asset_id
              ? { ...asset, delete_flag: currentFlag }
              : asset,
          ),
        );
        return;
      }
      const result = await res.json();
      // Update state with confirmed backend value just in case
      setAssets((prevAssets) =>
        prevAssets.map((asset) =>
          asset.emp_asset_id === emp_asset_id
            ? { ...asset, delete_flag: result.newFlag }
            : asset,
        ),
      );
      setSuccessMessage("Toggle flag updated Successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (error) {
      console.error("Toggle flag error:", error.message);
      // revert on error
      setAssets((prevAssets) =>
        prevAssets.map((asset) =>
          asset.emp_asset_id === emp_asset_id
            ? { ...asset, delete_flag: currentFlag }
            : asset,
        ),
      );
      setErrorMessage(error.message || "Delete failed..");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleReturnAsset = (row) => {
    setReturnRow({
      emp_asset_id: row.emp_asset_id,
      asset_id: row.asset_id,
      asset_description: row.asset_description,
      employee_id: row.employee_id,
      employee_name: row.employee_name,
    });
    setReturnConfirmOpen(true);
  };

  const confirmReturn = async () => {
    if (!returnRow) {
      console.error("No return row data");
      return;
    }

    // Validate required fields
    const isReplace = returnForm.asset_option === "Replace";
    const newErrors = {
      asset_option: !returnForm.asset_option,
      returned_to: !returnForm.returned_to,
      asset_complaints: !returnForm.asset_complaints,
      asset_id: isReplace && !returnForm.asset_id,
    };

    setReturnErrors(newErrors);

    // If any field has an error, stop the function
    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        emp_asset_id: returnRow.emp_asset_id,
        asset_id: returnRow.asset_id,
        employee_id: returnRow.employee_id,
        bo_id: bo_id,
        returned_by: empId,
        asset_option: returnForm.asset_option,
        returned_to: returnForm.returned_to, // Use selected employee from dropdown
        new_asset_id: returnForm.asset_id || null,
        asset_complaints: returnForm.asset_complaints || null,
        replaced_by: returnForm.asset_option === "Replace" ? empId : null,
        remarks: returnForm.remarks || null,
      };

      const response = await axios.post(`${BASE_URL}/asset/return`, payload, {
        withCredentials: true,
      });

      setSuccessMessage("Asset returned successfully");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (error) {
      console.error("Error returning asset:", error);
      console.error("Error response:", error.response?.data);
      setErrorMessage(error.response?.data?.error || "Failed to return asset");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setIsSubmitting(false);
      setReturnConfirmOpen(false);
      setReturnRow(null);
      setReturnForm({
        asset_option: "",
        returned_on: "",
        returned_to: "",
        asset_id: "",
        asset_complaints: "",
        replaced_on: "",
        remarks: "",
      });
      setReturnErrors({
        asset_option: false,
        returned_on: false,
        returned_to: false,
        asset_complaints: false,
        replaced_on: false,
        asset_id: false,
        remarks: "",
      });
    }
  };

  const handleCloseDialog = () => {
    setAssetDetails(null);
    setNewAsset({});
    setEditEmpAsset({});
    setOpen(false);
    setEditOpen(false);
    setIsEditing(false);
    setIsSubmitting(false); // Reset submitting state when closing dialog
  };
  return (
    <ThemeProvider theme={Table_theme}>
      <MainLayout>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {/* <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mt={2}
          mb={2}
          gap={2}
        ></Box> */}
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mt={0.5}
          mb={2}
          gap={2}
        >
          <IconButton onClick={(e) => setColumnAnchorEl(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>
          <Popover
            open={Boolean(columnAnchorEl)}
            anchorEl={columnAnchorEl}
            onClose={() => setColumnAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 280,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {empAssetFields.map((field) => (
              <MenuItem key={field} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {empfieldLabels[field]}
                  </Typography>
                  <Switch
                    size="small"
                    checked={columnVisibility[field]}
                    onChange={() =>
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [field]: !prev[field],
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#2DB0E4",
                        },
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Popover>
          {hasPermission("Employee Assets", "Edit") && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setAssetDetails(null);
                setNewAsset({});
                setErrors({});
                setOpen(true);
              }}
              sx={{
                fontWeight: "bold",
                borderRadius: "8px",
                px: 2.5,
                height: 36,
                backgroundColor: "#2DB0E4",
              }}
              startIcon={<Add />}
            >
              Add
            </Button>
          )}
          <TextField
            size="small"
            sx={{
              width: 250,
              "& input": { fontSize: 14 }, // input + placeholder
              "& input::placeholder": { fontSize: 12 }, // placeholder only
            }}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              sx: {
                height: 36, // Reduce overall input height
                paddingRight: 0,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* Status Dropdown */}
          <TextField
            select
            size="small"
            label="Status"
            value={filters.deleteFlag}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, deleteFlag: e.target.value }))
            }
            sx={{
              width: 200,
              // Make height match the 36px of the search field
              "& .MuiInputBase-root": {
                height: 36,
              },

              // Fix vertical alignment of text
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                fontSize: 12,
                padding: "0 14px",
              },

              // Label font size
              "& .MuiInputLabel-root": { fontSize: "14px" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "14px",
                      minHeight: "32px",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box
          sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}
        >
          <ThemeProvider theme={Table_theme}>
            <CssBaseline />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      Actions
                    </TableCell>

                    {empAssetFields
                      .filter((f) => columnVisibility[f])
                      .map((field) => (
                        <TableCell
                          key={field}
                          sx={{
                            whiteSpace: "nowrap",
                            textAlign: "left",
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ gap: 0.5 }}
                          >
                            <strong>{empfieldLabels[field]}</strong>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterIconClick(field, e)}
                            >
                              <FilterList fontSize="small" />
                            </IconButton>
                          </Box>

                          <Popover
                            open={Boolean(anchorEls[field])}
                            anchorEl={anchorEls[field]}
                            onClose={() => handleFilterClose(field)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Box
                              p={2}
                              display="flex"
                              flexDirection="column"
                              gap={1}
                              width={200}
                            >
                              <TextField
                                size="small"
                                fullWidth
                                placeholder={`Filter ${empfieldLabels[field]}`}
                                value={filters[field] || ""}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    [field]: e.target.value,
                                  }))
                                }
                              />
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleFilterClose(field)}
                                  sx={{ minWidth: 70 }}
                                >
                                  Apply
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setFilters((prev) => ({
                                      ...prev,
                                      [field]: "",
                                    }));
                                    handleFilterClose(field);
                                  }}
                                  sx={{ minWidth: 70 }}
                                >
                                  Clear
                                </Button>
                              </Box>
                            </Box>
                          </Popover>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assets.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip
                            title={
                              hasPermission("Employee Assets", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  hasPermission("Employee Assets", "Edit") &&
                                    setEditEmpAsset({
                                      ...row,
                                      employee_id: row.employee_id,
                                      asset_id: row.asset_id, // Explicitly set asset_id
                                      emp_asset_id: row.emp_asset_id,
                                    });
                                  setIsEditing(true);
                                  const currentAssetId = row.asset_id;
                                  try {
                                    // Wait for handleAssetChange to complete
                                    await handleAssetChange({
                                      target: { value: currentAssetId },
                                    });
                                    setEditOpen(true); // Open dialog only after handleAssetChange completes
                                  } catch (err) {
                                    console.error(
                                      "Error in handleAssetChange:",
                                      err,
                                    );
                                    setSnackbarMsg(
                                      "Failed to load asset details",
                                    );
                                    setSnackbarSeverity("error");
                                    setShowSnackbar(true);
                                  }
                                }}
                                disabled={
                                  !hasPermission("Employee Assets", "Edit")
                                }
                                sx={{ color: "#2DB0E4" }}
                              >
                                {" "}
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* <Tooltip
                            title={
                              hasPermission("Employee Assets", "Delete")
                                ? "Delete"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Assets", "Delete") &&
                                  handleDelete(
                                    row.emp_asset_id,
                                    row.asset_id,
                                    row.employee_name,
                                    row.asset_description,
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Assets", "Delete")
                                }
                                sx={{
                                  color: hasPermission(
                                    "Employee Assets",
                                    "Delete",
                                  )
                                    ? "#fc0404ff"
                                    : "#ccc",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip> */}
                          <Tooltip
                            title={
                              hasPermission("Employee Assets", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Clients", "Edit") &&
                                  handleToggleFlag(
                                    row.emp_asset_id,
                                    row.delete_flag,
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Assets", "Edit")
                                }
                              >
                                <Flag
                                  sx={{
                                    color:
                                      row.delete_flag === "N"
                                        ? "#2E9F50"
                                        : "#FF0C0C",
                                    width: 17,
                                    height: 17,
                                    transition: "color 0.2s",
                                    verticalAlign: "middle",
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Employee Assets", "Edit")
                                ? "Return Asset"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (
                                    hasPermission("Employee Assets", "Edit")
                                  ) {
                                    handleReturnAsset(row);
                                  }
                                }}
                                disabled={
                                  !hasPermission("Employee Assets", "Edit")
                                }
                                sx={{
                                  color: hasPermission(
                                    "Employee Assets",
                                    "Edit",
                                  )
                                    ? "#FF9800"
                                    : "#ccc",
                                }}
                              >
                                <AssignmentReturnIcon
                                  sx={{ fontSize: "16px" }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {empAssetFields
                        .filter((f) => columnVisibility[f])
                        .map((field) => (
                          <TableCell
                            key={field}
                            align="left"
                            sx={{
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {field === "warranty_validity"
                              ? row[field]
                                ? formatDateLocal(row[field])
                                : ""
                              : row[field]}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(totalCount / rowsPerPage)}
                page={page + 1} // Add +1 to match MUI pagination (1-based)
                onChange={(_, newPage) => setPage(newPage - 1)} // Subtract 1 to set back to 0-based index
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    minWidth: "32px",
                    height: "32px",
                    fontSize: "14px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#b3dbea !important",
                    color: "black",
                    fontWeight: "bold",
                  },
                  "& .MuiPaginationItem-ellipsis": {
                    display: "none",
                  },
                }}
              />
            </Box>
          </ThemeProvider>
        </Box>
        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              {`Are you sure you want to permanently delete ${assetName} for the
              employee ${empName}?`}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button onClick={confirmDelete} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Return Asset Dialog */}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Dialog
              open={returnConfirmOpen}
              onClose={(_, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                  return;
                }
                setReturnConfirmOpen(false);
                setReturnForm({
                  asset_option: "",
                  returned_on: "",
                  returned_to: "",
                  asset_id: "",
                  asset_complaints: "",
                  replaced_on: "",
                  remarks: "",
                });
                setReturnErrors({
                  asset_option: false,
                  returned_on: false,
                  returned_to: false,
                  asset_complaints: false,
                  replaced_on: false,
                  asset_id: false,
                  remarks: "",
                });
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle
                sx={{
                  borderRadius: "12px",
                  bgcolor: "#f5f5f5",
                  p: 1,
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                Return Asset
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    setReturnConfirmOpen(false);
                    setReturnForm({
                      asset_option: "",
                      returned_on: "",
                      returned_to: "",
                      asset_id: "",
                      asset_complaints: "",
                      replaced_on: "",
                      remarks: "",
                    });
                    setReturnErrors({
                      asset_option: false,
                      returned_on: false,
                      returned_to: false,
                      asset_complaints: false,
                      replaced_on: false,
                      asset_id: false,
                      remarks: "",
                    });
                  }}
                  sx={{ position: "absolute", right: 8, top: 5 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  rowGap: "8px",
                  columnGap: "10px",
                  p: "28px 32px 20px 32px",
                  alignItems: "start",
                  mt: 1,
                }}
              >
                {/* Row 1: Asset (col 1) | Asset Option (col 2) */}
                <TextField
                  label="Asset"
                  value={returnRow?.asset_description || ""}
                  InputProps={{ readOnly: true }} // better than disabled
                  sx={{
                    width: "260px",
                    "& .MuiOutlinedInput-root": {
                      height: "35px",
                      "& legend": {
                        maxWidth: "35px", //  removes extra notch space
                      },
                    },
                    "& .MuiInputLabel-outlined": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  }}
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label="Asset Option"
                  value={returnForm.asset_option || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setReturnForm((prev) => ({
                      ...prev,
                      asset_option: val,
                      ...(val === "Return"
                        ? { replaced_on: "", asset_id: "" }
                        : {}),
                    }));
                    setReturnErrors((prev) => ({
                      ...prev,
                      asset_option: false,
                    }));
                  }}
                  required
                  error={returnErrors.asset_option}
                  helperText={
                    returnErrors.asset_option ? "Asset Option is required" : ""
                  }
                  sx={{
                    width: "260px",
                    "& .MuiOutlinedInput-root": {
                      height: "35px",
                      "& legend": {
                        maxWidth: "70px", //  removes extra notch space
                      },
                    },
                    "& .MuiInputLabel-outlined": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  }}
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    MenuProps: {
                      disablePortal: true,
                      PaperProps: { style: { maxHeight: 250, width: 200 } },
                    },
                  }}
                >
                  <MenuItem value="Return">Return</MenuItem>
                  <MenuItem value="Replace">Replace</MenuItem>
                </TextField>
                {/* Row 2: Asset Complaints (col 1) | Asset dropdown (col 2) */}
                <TextField
                  select
                  label="Asset Complaints"
                  value={returnForm.asset_complaints}
                  onChange={(e) => {
                    setReturnForm((prev) => ({
                      ...prev,
                      asset_complaints: e.target.value,
                    }));
                    setReturnErrors((prev) => ({
                      ...prev,
                      asset_complaints: false,
                    }));
                  }}
                  required
                  error={returnErrors.asset_complaints}
                  helperText={
                    returnErrors.asset_complaints
                      ? "Asset Complaints is required"
                      : ""
                  }
                  margin="dense"
                  SelectProps={{ native: false }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: "260px",
                    "& .MuiOutlinedInput-root": {
                      height: "35px",
                      "& legend": {
                        maxWidth: "90px", //  removes extra notch space
                      },
                    },
                    "& .MuiInputLabel-outlined": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  }}
                >
                  {assetComplaintsOptions.map((opt) => (
                    <MenuItem
                      key={opt.variable_code || opt.code}
                      value={opt.variable_code || opt.code}
                      sx={{ fontSize: "12px" }}
                    >
                      {opt.variable_value || opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Asset"
                  value={returnForm.asset_id}
                  onChange={(e) => {
                    setReturnForm((prev) => ({
                      ...prev,
                      asset_id: e.target.value,
                    }));
                    setReturnErrors((prev) => ({ ...prev, asset_id: false }));
                  }}
                  disabled={returnForm.asset_option !== "Replace"}
                  required={returnForm.asset_option === "Replace"}
                  error={returnErrors.asset_id}
                  helperText={returnErrors.asset_id ? "Asset is required" : ""}
                  margin="dense"
                  SelectProps={{
                    native: false,
                    MenuProps: {
                      disablePortal: true,
                      PaperProps: { style: { maxHeight: 250, width: 280 } },
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: "260px",
                    "& .MuiOutlinedInput-root": {
                      height: "35px",
                      "& legend": {
                        maxWidth: "35px", //  removes extra notch space
                      },
                    },
                    "& .MuiInputLabel-outlined": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  }}
                >
                  {(() => {
                    // Extract asset type from current asset
                    const getCurrentAssetType = (desc) => {
                      if (!desc) return "";
                      return desc.trim().split(/[-\s]/)[0].toLowerCase();
                    };

                    const currentAsset = returnRow?.asset_description || "";
                    const currentType = getCurrentAssetType(currentAsset);

                    // Define PC equipment types (Monitor and Laptop are interchangeable)
                    const pcTypes = ["monitor", "laptop"];

                    // Filter assets based on type compatibility
                    const filteredAssets = returnAssetIdOptions.filter(
                      (opt) => {
                        const optType = getCurrentAssetType(
                          opt.asset_description || "",
                        );

                        // If current asset is Monitor or Laptop, show both Monitor and Laptop
                        if (pcTypes.includes(currentType)) {
                          return pcTypes.includes(optType);
                        }

                        // For other assets, show only same type
                        return currentType && optType === currentType;
                      },
                    );

                    return filteredAssets.map((opt) => (
                      <MenuItem
                        key={opt.asset_id}
                        value={opt.asset_id}
                        sx={{ fontSize: "12px" }}
                      >
                        {`${opt.asset_id} - ${opt.asset_description}`}
                      </MenuItem>
                    ));
                  })()}
                </TextField>
                {/* Row 3: Handed Over To (col 1) | Empty (col 2) */}
                <TextField
                  select
                  label="Handed Over To"
                  value={returnForm.returned_to || ""}
                  onChange={(e) => {
                    setReturnForm((prev) => ({
                      ...prev,
                      returned_to: e.target.value,
                    }));
                    setReturnErrors((prev) => ({
                      ...prev,
                      returned_to: false,
                    }));
                  }}
                  required
                  error={returnErrors.returned_to}
                  helperText={
                    returnErrors.returned_to ? "Handed Over To is required" : ""
                  }
                  margin="dense"
                  SelectProps={{
                    native: false,
                    MenuProps: {
                      disablePortal: true,
                      PaperProps: { style: { maxHeight: 250, width: 280 } },
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: "260px",
                    "& .MuiOutlinedInput-root": {
                      height: "35px",
                      "& legend": {
                        maxWidth: "85px", //  removes extra notch space
                      },
                    },
                    "& .MuiInputLabel-outlined": {
                      transform: "translate(12px, -9px) scale(0.75)",
                    },
                  }}
                >
                  {returnEmpIdOptions.map((opt) => (
                    <MenuItem
                      key={opt.employee_id}
                      value={opt.employee_id}
                      sx={{ fontSize: "12px" }}
                    >
                      {`${opt.employee_id} - ${opt.employee_name}`}
                    </MenuItem>
                  ))}
                </TextField>
                <Box /> {/* Empty placeholder for grid alignment */}
                {/* Row 4: Remarks (col 1, spans 2 columns) */}
                <TextField
                  label="Remarks (Optional)"
                  value={returnForm.remarks || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Limit to 255 characters with validation message
                    if (value.length <= 255) {
                      setReturnForm((prev) => ({
                        ...prev,
                        remarks: value,
                      }));
                      setReturnErrors((prev) => ({
                        ...prev,
                        remarks: "",
                      }));
                    } else {
                      // Show error message when trying to exceed limit
                      setReturnErrors((prev) => ({
                        ...prev,
                        remarks: "Maximum 255 characters allowed",
                      }));
                    }
                  }}
                  multiline
                  rows={2}
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  error={!!returnErrors.remarks}
                  helperText={returnErrors.remarks || " "}
                  sx={{
                    gridColumn: "span 2",

                    "& .MuiInputLabel-root": {
                      transform: "translate(12px, -9px) scale(0.75)",
                      transformOrigin: "top left",
                      backgroundColor: "#fff",
                      padding: "0 4px",
                      zIndex: 1,
                    },

                    "& .MuiOutlinedInput-root": {
                      minHeight: "90px",
                      alignItems: "flex-start",

                      "& textarea": {
                        padding: "8px 4px",
                        overflowY: "auto", // vertical scroll
                        overflowX: "hidden",
                        maxHeight: "70px",
                      },

                      "& legend": {
                        maxWidth: "95px",
                      },
                    },
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setReturnConfirmOpen(false);
                    setReturnForm({
                      asset_option: "",
                      returned_on: "",
                      returned_to: "",
                      asset_id: "",
                      asset_complaints: "",
                      replaced_on: "",
                      remarks: "",
                    });
                    setReturnErrors({
                      asset_option: false,
                      returned_on: false,
                      returned_to: false,
                      asset_complaints: false,
                      replaced_on: false,
                      asset_id: false,
                      remarks: "",
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmReturn}
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </DialogActions>
            </Dialog>
          </LocalizationProvider>

          <Dialog
            open={open || editOpen}
            onClose={(event, reason) => {
              // Prevent closing on backdrop click or escape key
              if (reason === "backdropClick" || reason === "escapeKeyDown") {
                return;
              }
              setOpen(false);
              setEditOpen(false);
              resetForm();
            }}
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: 5,
                overflow: "visible",
                width: "100%",
                maxWidth: { xs: "95vw", sm: "400px", md: "500px" },
              },
            }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {isEditing ? "Update Employee Asset" : "Add Employee Asset"}
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                p: "18px 24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <Grid container spacing={2} justifyContent="center">
                {/* Original fields */}
                {emppopupFields.map((field, index) => (
                  <Grid item xs={6}>
                    {field === "asset_id"
                      ? renderField(
                          field,
                          isEditing ? empEditAsset : newAsset,
                          isEditing ? setEditEmpAsset : setNewAsset,
                          isEditing,
                          handleAssetChange,
                        )
                      : renderField(
                          field,
                          isEditing ? empEditAsset : newAsset,
                          isEditing ? setEditEmpAsset : setNewAsset,
                          isEditing,
                        )}
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={2} justifyContent="center">
                {(isEditing || assetDetails) && (
                  <>
                    {/* Row 1 */}
                    <Grid item xs={6}>
                      <TextField
                        label="Model No"
                        value={assetDetails?.model_no || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Model Name"
                        value={assetDetails?.model_name || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Serial No"
                        value={assetDetails?.serial_no || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        disabled
                        InputLabelProps={{ shrink: true }} // forces label above
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Warranty Validity"
                        value={assetDetails?.warranty_validity ?? ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Condition"
                        value={assetDetails?.asset_condition || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Warranty Status"
                        value={assetDetails?.warranty_status || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={isEditing ? handleEdit : handleAdd}
                variant="contained"
                color="primary"
                disabled={!isEditing && isSubmitting}
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 2.5,
                  height: 36,
                  ...(isEditing
                    ? {
                        backgroundColor: "#4CAF50",
                        "&:hover": { backgroundColor: "#45a049" },
                      }
                    : {
                        backgroundColor: "#2DB0E4",
                        "&:hover": { backgroundColor: "#1D87B0" },
                      }),
                }}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={showSnackbar}
            autoHideDuration={5000}
            onClose={() => setShowSnackbar(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setShowSnackbar(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMsg}
            </Alert>
          </Snackbar>
        </ThemeProvider>
      </MainLayout>
    </ThemeProvider>
  );
};
export default EmployeeAssets;
